import React, { useContext, useEffect, useRef, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { UserContext } from "../../../amplify/authenticator-provider";
import {
  GET_GENERIC_FILTERS,
  GET_GENERIC_FILTER_TYPES,
  GET_PERMISSION_ACTION_GROUP,
} from "../../../graphql/query";
import { CREATE_GENERIC_FILTER, UPDATE_GENERIC_FILTER } from "../../../graphql/mutation";
import CustomCheckboxDropdown from "../../commonComponents/CustomCheckboxDropdown";
import useLocation from "../../../hooks/useLocation";

const VEHICLE_INVENTORY_LOCATION_ACCESS = "VEHICLE_INVENTORY_LOCATION_ACCESS";

/**
 * VdpLocationSearch
 *
 * Encapsulates all location-fetching and generic-filter-persistence logic.
 * Renders a CustomCheckboxDropdown for location selection.
 *
 * Props:
 *   onChange          {Function}  Called with the full selectedLocations array on every change
 *   onLoadingChange   {Function}  Called with true/false as location queries load/finish
 *   dropdownClassName {String}    Custom class name for the dropdown
 */
const VdpLocationSearch = ({ onChange, onLoadingChange, dropdownClassName }) => {
  const { user } = useContext(UserContext);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [genericFilterDataValues, setGenericFilterDataValues] = useState({});
  const [genericFilterTypesData, setGenericFilterTypesData] = useState([]);
  const isInitialized = useRef(false);

  const currentLocationId = +sessionStorage.getItem("currentLocationId");

  // Fetch the company's locations (source of the dropdown list) instead of
  // relying on user.locations, so locations the user isn't directly assigned to
  // but is permitted for still appear.
  const { data: companyLocationsData, loading: companyLocationsLoading } = useLocation({
    variables: { companyId: user ? +user?.company?.id : null },
    skip: !user?.company?.id,
    fetchPolicy: "cache-first",
  });

  const [updateGenericFilter] = useMutation(UPDATE_GENERIC_FILTER);
  const [createGenericFilter] = useMutation(CREATE_GENERIC_FILTER);

  const [getGenericFilterTypes, { loading: genericFilterTypeLoader }] = useLazyQuery(
    GET_GENERIC_FILTER_TYPES,
    { fetchPolicy: "cache-first" }
  );

  const [getGenericFilters, { loading: genericFilterLoader }] = useLazyQuery(GET_GENERIC_FILTERS, {
    fetchPolicy: "network-only",
  });

  // Fetch permission actions to get allowed location IDs
  const [permittedLocationIds, setPermittedLocationIds] = useState(null);

  useQuery(GET_PERMISSION_ACTION_GROUP, {
    fetchPolicy: "network-only",
    variables: { userId: user ? parseInt(user?.id) : null },
    skip: !user?.id,
    onCompleted: res => {
      const groups = res?.getPermissionActionGroup || [];
      const actions = groups.flatMap(g => g.permissionObj?.permissionActions || []);
      const match = actions.find(a => a.actionCode === VEHICLE_INVENTORY_LOCATION_ACCESS);
      setPermittedLocationIds(
        match?.extraData?.location_ids?.length ? match.extraData.location_ids.map(id => +id) : []
      );
    },
    onError: () => {
      setPermittedLocationIds([]);
    },
  });

  const initLocations = async () => {
    // 1. Build location list: default location always included + permitted locations.
    // Source is the company's locations (fetched via useLocation).
    const allUserLocations = (companyLocationsData?.getLocations || []).map(loc => ({
      id: +loc.id,
      title: loc.title,
    }));

    let temp;
    if (permittedLocationIds.length > 0) {
      const allowedIds = new Set([currentLocationId, ...permittedLocationIds]);
      temp = allUserLocations.filter(loc => allowedIds.has(loc.id));
    } else {
      temp = allUserLocations.filter(loc => loc.id === currentLocationId);
    }

    if (temp.length > 1) {
      temp.unshift({ id: 0, title: "All Locations" });
    }

    // 2. Generic filter persistence
    const genericFilterTypes = await getGenericFilterTypes();
    const filterTypeList = genericFilterTypes?.data?.getGenericFilterTypes || [];

    if (filterTypeList.length === 0) return;

    setGenericFilterTypesData(filterTypeList);
    const vehicleFilterType = filterTypeList.find(
      item => item?.code === "VEHICLE_INVENTORY_FILTER"
    );

    const genericFilterData = await getGenericFilters({
      variables: {
        filterName: vehicleFilterType?.name,
        genericFilterTypeId: vehicleFilterType?.id,
      },
    });

    if (!genericFilterData?.data?.getGenericFilters?.length) {
      // No saved filter — precheck default location
      const updatedLocations = temp.map(loc =>
        +loc.id === currentLocationId ? { ...loc, isChecked: true } : loc
      );
      setSelectedLocations(updatedLocations);
      onChange?.(updatedLocations);

      await createGenericFilter({
        variables: {
          filterJson: JSON.stringify(updatedLocations),
          filterName: vehicleFilterType?.name,
          genericFilterTypeId: vehicleFilterType?.id,
        },
        onCompleted: createRes => {
          if (createRes?.createGenericFilter?.statusCode) {
            const locations = JSON.parse(
              createRes.createGenericFilter.genericFilters.genericFilterJson.filterJson
            );
            setSelectedLocations(locations);
            setGenericFilterDataValues(createRes.createGenericFilter.genericFilters);
            onChange?.(locations);
          }
        },
      });
    } else {
      // Saved filter exists — restore but only keep locations that are in our allowed temp list
      const savedLocations = JSON.parse(
        genericFilterData.data.getGenericFilters[0].genericFilterJson.filterJson
      );
      const allowedIdSet = new Set(temp.map(l => l.id));
      const filteredSaved = savedLocations.filter(loc => allowedIdSet.has(loc.id));

      // If saved filter is missing some allowed locations, add them unchecked
      const savedIdSet = new Set(filteredSaved.map(l => l.id));
      temp.forEach(loc => {
        if (!savedIdSet.has(loc.id)) {
          filteredSaved.push({ ...loc, isChecked: loc.id === currentLocationId });
        }
      });

      // Ensure default location is always checked
      const finalLocations = filteredSaved.map(loc =>
        loc.id === currentLocationId ? { ...loc, isChecked: true } : loc
      );

      setSelectedLocations(finalLocations);
      setGenericFilterDataValues(genericFilterData.data.getGenericFilters[0]);
      onChange?.(finalLocations);

      // Persist back to DB if permissions changed the allowed set (revoked or newly granted)
      const savedIds = savedLocations.map(l => l.id).sort();
      const finalIds = finalLocations.map(l => l.id).sort();
      const idsChanged =
        savedIds.length !== finalIds.length || savedIds.some((id, i) => id !== finalIds[i]);

      if (idsChanged) {
        updateGenericFilter({
          variables: {
            filterJson: JSON.stringify(finalLocations),
            filterName: vehicleFilterType?.name,
            genericFilterTypeId: genericFilterData.data.getGenericFilters[0].genericFilterTypeId,
            id: genericFilterData.data.getGenericFilters[0].id,
          },
        });
      }
    }
  };

  // Build locations once company locations + permission data are ready
  useEffect(() => {
    if (
      isInitialized.current ||
      !companyLocationsData?.getLocations?.length ||
      permittedLocationIds === null
    )
      return;
    isInitialized.current = true;
    initLocations();
  }, [companyLocationsData, permittedLocationIds]);

  // Bubble combined loading state up to parent
  useEffect(() => {
    onLoadingChange?.(genericFilterLoader || genericFilterTypeLoader || companyLocationsLoading);
  }, [genericFilterLoader, genericFilterTypeLoader, companyLocationsLoading]);

  const handleChange = newValues => {
    // Rebuild selectedLocations from the new value array
    let updatedLocations = selectedLocations.map(loc => {
      if (loc.id === 0) return loc; // sync id=0 below
      return { ...loc, isChecked: newValues.includes(loc.id) };
    });

    // Sync "All Locations" (id=0) — checked only when every other location is checked
    const allNonZeroChecked = updatedLocations.filter(l => l.id !== 0).every(l => l.isChecked);

    updatedLocations = updatedLocations.map(loc =>
      loc.id === 0 ? { ...loc, isChecked: allNonZeroChecked } : loc
    );

    const vehicleFilterType = genericFilterTypesData.find(
      item => item?.code === "VEHICLE_INVENTORY_FILTER"
    );

    updateGenericFilter({
      variables: {
        filterJson: JSON.stringify(updatedLocations),
        filterName: vehicleFilterType?.name,
        genericFilterTypeId: genericFilterDataValues?.genericFilterTypeId,
        id: genericFilterDataValues?.id,
      },
    });

    setSelectedLocations(updatedLocations);
    onChange?.(updatedLocations);
  };

  // Map to CustomCheckboxDropdown format (exclude id=0 — handled by Select All)
  const locationOptions = selectedLocations
    .filter(loc => loc.id !== 0)
    .map(loc => ({ label: loc.title, value: loc.id }));

  const selectedValues = selectedLocations
    .filter(loc => loc.isChecked && loc.id !== 0)
    .map(loc => loc.id);

  return (
    <CustomCheckboxDropdown
      options={locationOptions}
      value={selectedValues}
      onChange={handleChange}
      placeholder="Locations"
      disabledValues={[currentLocationId]}
      searchPlaceholder="Search locations"
      dropdownClassName={dropdownClassName}
    />
  );
};

export default VdpLocationSearch;
